const DISPLAY_DATE_PATTERN = /^(\d{1,2})\/(\d{1,2})$/;

// No year in the flag value, so February allows the 29th (leap-year day).
const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/**
 * A `web-sunset-date` flag value only drives the post-login sunset modal
 * when it is a well-formed calendar-valid dd/mm date — the exact format
 * rendered in the copy (e.g. "20/11"). Anything else — empty string, "off",
 * ISO dates, garbage — means "do not show the modal".
 *
 * Takes `unknown` on purpose: the Vercel Flags adapter is a runtime
 * boundary whose payloads are not guaranteed to honor the TypeScript
 * `string` type, and an unguarded `.trim()` would throw from the root
 * layout render, 500ing every route while the malformed value persists.
 */
function parseDisplayDate(
  value: unknown,
): { day: string; month: string } | null {
  if (typeof value !== 'string') return null;
  const match = DISPLAY_DATE_PATTERN.exec(value.trim());
  if (!match) return null;
  const day = Number(match[1]);
  const month = Number(match[2]);
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > DAYS_IN_MONTH[month - 1]) return null;
  return { day: match[1], month: match[2] };
}

export function isValidWebSunsetDate(value: unknown): boolean {
  return parseDisplayDate(value) !== null;
}

/**
 * Normalizes a valid dd/mm flag value to zero-padded dd/mm ("5/1" → "05/01",
 * "20/11" → "20/11"). The flag value is the display string itself — there
 * is no date parsing anywhere (ISO strings would parse as UTC midnight and
 * render the previous day in America/Sao_Paulo). Returns '' for anything
 * invalid (non-string, wrong shape, impossible day/month) — callers treat
 * that as "hide the announcement".
 */
export function formatWebSunsetDate(value: unknown): string {
  const parsed = parseDisplayDate(value);
  if (!parsed) return '';
  return `${parsed.day.padStart(2, '0')}/${parsed.month.padStart(2, '0')}`;
}
